import { onMounted, ref, watch, type Ref } from "vue";

export const useDynamicFavicon = (merchantLogoUrl: Ref<string | null>) => {
  const defaultFaviconHref = ref<string>("");
  const canonicalLink = ref<HTMLLinkElement | null>(null);
  const cachedDataUrls = new Map<string, string>();
  const isReady = ref(false);
  let requestToken = 0;

  const ensureCanonicalLink = () => {
    if (canonicalLink.value) return canonicalLink.value;

    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
    );

    if (links.length > 0) {
      canonicalLink.value = links[0];
      const rawHref = canonicalLink.value.getAttribute("href");
      defaultFaviconHref.value =
        rawHref || canonicalLink.value.href || "/favicon.ico";
      links.slice(1).forEach((link) => link.remove());
    } else {
      const link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
      canonicalLink.value = link;
      defaultFaviconHref.value = "/favicon.ico";
    }

    if (!defaultFaviconHref.value) {
      defaultFaviconHref.value = "/favicon.ico";
    }

    if (!canonicalLink.value?.getAttribute("href")) {
      canonicalLink.value?.setAttribute("href", defaultFaviconHref.value);
    }

    return canonicalLink.value;
  };

  const restoreDefault = (token: number) => {
    if (token !== requestToken) return;
    const link = ensureCanonicalLink();
    link.setAttribute("href", defaultFaviconHref.value || "/favicon.ico");
    link.removeAttribute("type");
  };

  const applyDataUrl = (dataUrl: string, token: number) => {
    if (token !== requestToken) return;
    const link = ensureCanonicalLink();
    link.setAttribute("href", dataUrl);
    link.setAttribute("type", "image/png");
  };

  const convertToDataUrl = (logoUrl: string, token: number) =>
    new Promise<string>((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => {
        if (token !== requestToken) {
          reject(new Error("Stale favicon request"));
          return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Canvas context unavailable"));
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        try {
          resolve(canvas.toDataURL("image/png"));
        } catch (error) {
          reject(error);
        }
      };
      image.onerror = () => reject(new Error("Image failed to load"));
      image.src = logoUrl;
    });

  const updateFavicon = async (logoUrl: string | null) => {
    const token = ++requestToken;

    if (!logoUrl) {
      restoreDefault(token);
      return;
    }

    const cached = cachedDataUrls.get(logoUrl);
    if (cached) {
      applyDataUrl(cached, token);
      return;
    }

    try {
      const dataUrl = await convertToDataUrl(logoUrl, token);
      if (token !== requestToken) return;
      cachedDataUrls.set(logoUrl, dataUrl);
      applyDataUrl(dataUrl, token);
    } catch (error) {
      restoreDefault(token);
    }
  };

  onMounted(() => {
    ensureCanonicalLink();
    isReady.value = true;
  });

  watch(
    () => [merchantLogoUrl.value, isReady.value] as const,
    ([logoUrl, ready]) => {
      if (!ready) return;
      updateFavicon(logoUrl);
    },
    { immediate: true }
  );
};

/**
 * Manual test checklist:
 * - [ ] Load the app without a merchant logo URL and confirm the default favicon stays.
 * - [ ] Switch to a merchant with a valid logo and confirm the favicon updates.
 * - [ ] Break the logo URL or simulate CORS errors and confirm the favicon reverts.
 */
